import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useCollaborations } from '../context/CollaborationsContext';
import { apiUpload, apiFetch, apiDownload } from '../api';
import { normalizeAttachment } from '../utils/helpers';

/**
 * Gestiona un adjunto de una colaboración (contrato, firmado, factura).
 *
 * Todo pasa por /api/collaborations/:id/files/:kind — ver server/routes/files.js.
 * El fichero se guarda siempre; el aviso por correo puede fallar aparte, y por
 * eso se expone `resend`: reintentar no obliga a volver a subir nada.
 *
 * `messages` describe los textos de cada tipo:
 *   uploaded(filename) → todo salió bien
 *   uploadedButFailed  → guardado pero sin avisar
 *   resent             → reintento correcto
 *   failedShort        → etiqueta corta para la tarjeta
 */
export function useAttachment(collab, kind, messages) {
  const { applyCollaboration } = useCollaborations();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const file = normalizeAttachment(collab[kind]);
  const basePath = `/api/collaborations/${collab.id}/files/${kind}`;

  async function handleUpload(e) {
    const picked = e.target.files[0];
    e.target.value = '';
    if (!picked) return;

    const formData = new FormData();
    formData.append('file', picked);
    setBusy(true);
    try {
      const { collaboration, notification } = await apiUpload(basePath, formData);
      applyCollaboration(collaboration);
      if (notification.status === 'sent') {
        toast.success(messages.uploaded(picked.name));
      } else {
        toast.warning(messages.uploadedButFailed, { description: notification.error });
      }
    } catch (err) {
      toast.error(`No se pudo subir el archivo: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    try {
      const { collaboration, notification } = await apiFetch(`${basePath}/notify`, {
        method: 'POST',
      });
      applyCollaboration(collaboration);
      if (notification.status === 'sent') toast.success(messages.resent);
      else toast.error(`Sigue fallando: ${notification.error}`);
    } catch (err) {
      toast.error(`No se pudo reenviar: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    try {
      await apiDownload(basePath, file?.filename);
    } catch (err) {
      toast.error(`No se pudo descargar: ${err.message}`);
    }
  }

  return {
    file,
    busy,
    inputRef,
    messages,
    pick: () => inputRef.current?.click(),
    handleUpload,
    resend,
    download,
  };
}
